/**
 * Threshold values for touch interactions
 */
const THRESHOLD = { DRAG: 5, SWIPE: 50 };

/**
 * Extracts touch coordinates from a touch event
 *
 * @param {TouchEvent} event The touch event
 *
 * @returns {Object} Object containing x and y coordinates
 */
const getTouchCoordinates = (event) => ({ x: event.touches[0].clientX, y: event.touches[0].clientY });

/**
 * Determines if a touch movement is a swipe gesture
 *
 * @param {number} diffX The horizontal distance moved
 * @param {number} diffY The vertical distance moved
 *
 * @returns {boolean} True if the gesture is a swipe. False otherwise
 */
const isSwipeGesture = (diffX, diffY) => Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > THRESHOLD.SWIPE;

/**
 * Determines if a touch movement is a drag action
 *
 * @param {number} moveDistance The distance moved
 *
 * @returns {boolean} True if the movement is a drag action. False otherwise
 */
const isDragAction = (moveDistance) => Math.abs(moveDistance) > THRESHOLD.DRAG;

/**
 * Gets the next index in a circular list
 *
 * @param {number} currentIndex The current index
 * @param {number} total        The total number of items
 *
 * @returns {number} The next index
 */
const getNextItemIndex = (currentIndex, total) => (currentIndex + 1) % total;

/**
 * Gets the previous index in a circular list
 *
 * @param {number} currentIndex The current index
 * @param {number} total        The total number of items
 *
 * @returns {number} The previous index
 */
const getPreviousItemIndex = (currentIndex, total) => (currentIndex - 1 + total) % total;

/**
 * Removes active class from all thumbnails
 *
 * @param {Object} elements The carousel elements
 */
const clearActiveThumbnail = (elements) =>
  Array.from(elements.thumbnailItems).forEach((thumbnail) => thumbnail.classList.remove('thumbnail-active'));

/**
 * Updates the main image with thumbnail data
 *
 * @param {HTMLImageElement} mainImage The main image element
 * @param {HTMLElement}      thumbnail The thumbnail element
 */
const updateMainImage = (mainImage, thumbnail) => {
  mainImage.src = thumbnail.dataset.fullSrc;
  mainImage.alt = thumbnail.alt;
};

/**
 * Parses and injects video embed code
 *
 * @param {HTMLElement} container The container to inject the video
 * @param {string}      embedCode The HTML embed code
 */
const parseAndInjectVideo = (container, embedCode) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(embedCode, 'text/html');
  container.innerHTML = '';

  const responsivePadding = doc.querySelector('.wistia_responsive_padding');
  container.innerHTML = responsivePadding ? responsivePadding.outerHTML : embedCode;

  Array.from(doc.getElementsByTagName('script')).forEach((script) => {
    const newScript = document.createElement('script');
    newScript.src = script.src;
    newScript.async = true;
    container.appendChild(newScript);
  });
};

/**
 * Updates the carousel position CSS variables
 *
 * @param {Object} elements The carousel elements
 * @param {number} index    The current index
 * @param {number} total    The total number of items
 */
const updateCarouselPosition = (elements, index, total) => {
  const offset = index >= 1 ? (index > total - 2 ? -2 : -1) : 0;

  elements.carousel.style.setProperty('--offset', offset.toString());
  elements.carousel.style.setProperty('--current', index.toString());
};

/**
 * Displays image content in the carousel
 *
 * @param {Object}      elements         The carousel elements
 * @param {HTMLElement} thumbnailElement The thumbnail element
 */
const displayImageContent = (elements, thumbnailElement) => {
  updateMainImage(elements.mainImage, thumbnailElement);
  elements.mainImageContainer.style.display = 'flex';
  elements.videoContainer.classList.add('hidden');
};

/**
 * Displays video content in the carousel
 *
 * @param {Object} elements  The carousel elements
 * @param {string} embedCode The HTML embed code
 */
const displayVideoContent = (elements, embedCode) => {
  parseAndInjectVideo(elements.videoContainer, embedCode);
  elements.mainImageContainer.style.display = 'none';
  elements.videoContainer.classList.remove('hidden');
};

/**
 * Activates a thumbnail and displays its content
 *
 * @param {Object}      elements  The carousel elements
 * @param {HTMLElement} thumbnail The thumbnail to activate
 */
const activateThumbnailItem = (elements, thumbnail) => {
  elements.thumbnails.style.transform = '';

  clearActiveThumbnail(elements);
  thumbnail.classList.add('thumbnail-active');

  const videoEmbed = thumbnail.getAttribute('data-wistia-video');
  videoEmbed ? displayVideoContent(elements, videoEmbed) : displayImageContent(elements, thumbnail);

  const index = Array.from(elements.thumbnailItems).indexOf(thumbnail);
  updateCarouselPosition(elements, index, elements.thumbnailItems.length);
};

/**
 * Navigates to the next item in the carousel
 *
 * @param {Object} elements The carousel elements
 */
const navigateToNextItem = (elements) => {
  const currentIndex = Array.from(elements.thumbnailItems).indexOf(
    elements.carousel.querySelector('.thumbnail-active'),
  );
  const nextIndex = getNextItemIndex(currentIndex, elements.thumbnailItems.length);

  activateThumbnailItem(elements, elements.thumbnailItems[nextIndex]);
};

/**
 * Navigates to the previous item in the carousel
 *
 * @param {Object} elements The carousel elements
 */
const navigateToPreviousItem = (elements) => {
  const currentIndex = Array.from(elements.thumbnailItems).indexOf(
    elements.carousel.querySelector('.thumbnail-active'),
  );
  const prevIndex = getPreviousItemIndex(currentIndex, elements.thumbnailItems.length);

  activateThumbnailItem(elements, elements.thumbnailItems[prevIndex]);
};

/**
 * Creates a touch start event handler
 *
 * @param {Object} state The touch state object
 *
 * @returns {Function} The touch start handler function
 */
const createTouchStartHandler = (state) => (event) => {
  state.touchStartX = getTouchCoordinates(event).x;
  state.currentTranslate =
    event.currentTarget.getBoundingClientRect().left - event.currentTarget.parentElement.getBoundingClientRect().left;
  event.currentTarget.style.transition = 'none';
};

/**
 * Creates a touch move event handler
 *
 * @param {Object} state The touch state object
 *
 * @returns {Function} The touch move handler function
 */
const createTouchMoveHandler = (state) => (event) => {
  if (!state.touchStartX) {
    return;
  }

  const moveDistance = state.touchStartX - event.touches[0].clientX;

  if (isDragAction(moveDistance)) {
    const minTranslate = event.currentTarget.parentElement.clientWidth - event.currentTarget.scrollWidth;

    let newTranslate = state.currentTranslate - moveDistance;
    newTranslate = newTranslate > 0 ? 0 : newTranslate;
    newTranslate = newTranslate < minTranslate ? minTranslate : newTranslate;

    event.currentTarget.style.transform = `translateX(${newTranslate}px)`;
  }
};

/**
 * Creates a touch end event handler
 *
 * @param {Object} state The touch state object
 *
 * @returns {Function} The touch end handler function
 */
const createTouchEndHandler = (state) => (event) => {
  state.touchStartX = null;
  event.currentTarget.style.transition = '';
};

/**
 * Creates a content touch start event handler
 *
 * @param {Object} state The touch state object
 *
 * @returns {Function} The content touch start handler function
 */
const createContentTouchStartHandler = (state) => (event) => {
  const coords = getTouchCoordinates(event);
  state.touchStartX = coords.x;
  state.touchStartY = coords.y;
};

/**
 * Creates a content touch end event handler
 *
 * @param {Object} state    The touch state object
 * @param {Object} elements The carousel elements
 *
 * @returns {Function} The content touch end handler function
 */
const createContentTouchEndHandler = (state, elements) => (event) => {
  if (!state.touchStartX || !state.touchStartY) {
    return;
  }

  const diffX = state.touchStartX - event.changedTouches[0].clientX;
  const diffY = state.touchStartY - event.changedTouches[0].clientY;

  if (isSwipeGesture(diffX, diffY)) {
    diffX > 0 ? navigateToNextItem(elements) : navigateToPreviousItem(elements);
  }

  state.touchStartX = null;
  state.touchStartY = null;
};

/**
 * Attaches touch event handlers to thumbnails container
 *
 * @param {HTMLElement} thumbnails The thumbnails container element
 */
const attachThumbnailsTouchEvents = (thumbnails) => {
  let touchState = { touchStartX: null, currentTranslate: 0 };

  thumbnails.addEventListener('touchstart', createTouchStartHandler(touchState), { passive: true });
  thumbnails.addEventListener('touchmove', createTouchMoveHandler(touchState), { passive: true });
  thumbnails.addEventListener('touchend', createTouchEndHandler(touchState), { passive: true });
};

/**
 * Attaches click event handlers to thumbnails
 *
 * @param {Object} elements The carousel elements
 */
const attachThumbnailsClickEvents = (elements) => {
  Array.from(elements.thumbnailItems).forEach((thumbnail) => {
    thumbnail.addEventListener('click', () => activateThumbnailItem(elements, thumbnail));
  });
};

/**
 * Attaches touch event handlers to the main content area
 *
 * @param {HTMLElement} container The container element
 * @param {Object}      elements  The carousel elements
 */
const attachMainContainerTouchEvents = (container, elements) => {
  let touchState = { touchStartX: null, touchStartY: null };

  container.addEventListener('touchstart', createContentTouchStartHandler(touchState), { passive: true });
  container.addEventListener('touchend', createContentTouchEndHandler(touchState, elements), { passive: true });
};

/**
 * Sets up window resize event handler
 *
 * @param {HTMLElement} thumbnails The thumbnails container element
 */
const setupResizeHandler = (thumbnails) => window.addEventListener('resize', () => (thumbnails.style.transform = ''));

/**
 * Initializes the active content
 *
 * @param {Object} elements The carousel elements
 */
const initializeActiveContent = (elements) => {
  const activeThumbnail = elements.carousel.querySelector('.thumbnail-active');
  const videoEmbed = activeThumbnail.getAttribute('data-wistia-video');
  const index = Array.from(elements.thumbnailItems).indexOf(activeThumbnail);

  activeThumbnail.classList.add('thumbnail-active');
  videoEmbed ? displayVideoContent(elements, videoEmbed) : displayImageContent(elements, activeThumbnail);
  updateCarouselPosition(elements, index, elements.thumbnailItems.length);
};

/**
 * Initializes a gallery carousel component
 *
 * @param {HTMLElement} carouselContainer The carousel container element
 */
export default function initGalleryCarousel(carouselContainer) {
  const elements = {
    carousel: carouselContainer,
    thumbnails: carouselContainer.querySelector('.thumbnails'),
    thumbnailItems: carouselContainer.querySelectorAll('.thumbnail'),
    mainImageContainer: carouselContainer.querySelector('.main-image-container'),
    mainImage: carouselContainer.querySelector('.main-image'),
    videoContainer: carouselContainer.querySelector('.carousel-video-container'),
  };

  attachThumbnailsClickEvents(elements);
  attachThumbnailsTouchEvents(elements.thumbnails);
  attachMainContainerTouchEvents(elements.mainImageContainer, elements);
  attachMainContainerTouchEvents(elements.videoContainer, elements);
  setupResizeHandler(elements.thumbnails);
  initializeActiveContent(elements);
}
